import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePlan } from '../contexts/PlanContext';
import { useNavigate } from 'react-router-dom';
import { getProjects, deleteProject } from '../services/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Building2, Plus, Loader2, Search, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { ProjectCard } from './projects/ProjectCard';
import { ProjectFormDialog } from './projects/ProjectFormDialog';

const PLACEHOLDER_IMAGES = [
  'https://images.unsplash.com/photo-1706341801850-18eb293a8017?crop=entropy&cs=srgb&fm=jpg&w=400&h=300&fit=crop',
  'https://images.unsplash.com/photo-1602749302182-57c68021c61e?crop=entropy&cs=srgb&fm=jpg&w=400&h=300&fit=crop',
  'https://images.unsplash.com/photo-1632398461363-741a64493ab6?crop=entropy&cs=srgb&fm=jpg&w=400&h=300&fit=crop'
];

const ProjectsPage = () => {
  const { user } = useAuth();
  const { canCreateProject, usage, isReadOnly } = usePlan();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);

  const loadProjects = useCallback(async () => {
    try {
      const data = await getProjects();
      setProjects(data);
    } catch (error) {
      console.error('Failed to load projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const handleOpenDialog = (project = null) => {
    setEditingProject(project || null);
    setIsDialogOpen(true);
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    try {
      await deleteProject(projectId);
      toast.success('Project deleted successfully');
      loadProjects();
    } catch (error) {
      console.error('Failed to delete project:', error);
      toast.error(error.message || 'Failed to delete project');
    }
  };

  const filteredProjects = projects.filter(p => 
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (p.location && p.location.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in" data-testid="projects-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Projects</h1>
          <p className="text-muted-foreground mt-1">
            Manage your real estate projects and inventory
          </p>
        </div>
        
        {(user?.role === 'admin' || user?.role === 'management') && !isReadOnly && (
          canCreateProject ? (
            <Button onClick={() => handleOpenDialog()} data-testid="add-project-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Project
            </Button>
          ) : (
            <Button variant="outline" onClick={() => navigate('/request-pricing')} data-testid="upgrade-project-btn">
              <Lock className="w-4 h-4 mr-2" />
              Upgrade to Add ({usage?.project_count}/{usage?.max_projects})
            </Button>
          )
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search projects..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="search-projects"
        />
      </div>

      {/* Projects Grid */}
      {filteredProjects.length === 0 ? (
        <Card className="p-12 text-center">
          <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No projects found</h3>
          <p className="text-muted-foreground mb-4">
            {searchQuery ? 'Try a different search term' : 'Get started by creating your first project'}
          </p>
          {!searchQuery && (user?.role === 'admin' || user?.role === 'management') && (
            <Button onClick={() => handleOpenDialog()}>
              <Plus className="w-4 h-4 mr-2" />
              Add Project
            </Button>
          )}
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project, index) => (
            <ProjectCard
              key={project.project_id}
              project={project}
              index={index}
              placeholderImages={PLACEHOLDER_IMAGES}
              user={user}
              isReadOnly={isReadOnly}
              onEdit={handleOpenDialog}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* Project Form Dialog */}
      <ProjectFormDialog
        isOpen={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        editingProject={editingProject}
        developerId={user?.developer_id || 'dev_default'}
        onSaved={loadProjects}
      />
    </div>
  );
};

export default ProjectsPage;
